import { Grid2, Paper, Typography, Tooltip, Button } from '@mui/material';
import { withStyles } from '@mui/styles';
import PublishIcon from '@mui/icons-material/Publish';
import { useCallback, useEffect, useState, useContext, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import heic2any from 'heic2any';
import { COLORS } from '../../../utils/Application_Constants';
import FilePreview from './FilePreview';
import FileRejections from './FileRejections';
import { postImageForOCR, deleteExpenseImageById } from '../../Services/ExpenseService';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { ValidationModeContext } from '../../../contexts/VendorPaymentValidationModeContext';
import { BillTypeEnum } from './BillType.enum';
import { formatErrorMessage } from '../../Utils/ErrorMessage';
import { ExpenseModeEnum } from './ExpenseMode.enum';

const styles = (theme) => ({
    PaperRoot: {
        padding: '24px',
        background: 'white',
        marginTop: 41,
    },
    formControl: {
        marginBottom: theme.spacing(1),
    },
    '& input[type="file"]': {
        display: 'none',
    },
    outside: {
        borderStyle: 'dashed',
        borderWidth: '2px',
        borderColor: COLORS.LT_JOURNEY25,
        textAlign: 'center',
        borderRadius: '8px',
        cursor: 'pointer',
        opacity: '1',
        background: COLORS.LT_JOURNEY5,
        gap: '16px',
        '&:hover': {
            background: COLORS.LT_MIDNIGHTBG2,
        },

        '&.disabled': {
            '&:hover': {
                background: 'transparent',
            },
            cursor: 'initial',
            opacity: 0.5,
        },
    },
    header: {
        paddingBottom: '10px',
    },
    dot: {
        height: '50px',
        width: '50px',
        backgroundColor: COLORS.LT_JOURNEY10,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: COLORS.LT_JOURNEY,
    },
    uploadTitle: {
        '& u': {
            color: COLORS.LT_JOURNEY,
            fontWeight: 'bold',
        },
    },
    uploadDescription: {
        fontSize: '12px',
        marginTop: '8px',
        color: COLORS.LT_MIDNIGHT50,
    },
    centerDrop: {
        padding: 24,
        minHeight: 150,
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 8,
    },
    tip: {
        marginTop: 24,
    },
    standardInfo: {
        background: COLORS.LT_MIDNIGHTSTEEL,
        color: COLORS.LT_MIDNIGHT75,
        fontSize: 12,
        lineHeight: 1.25,
        opacity: 0.8,
    },
    standardInfoIcon: {
        color: COLORS.LT_MIDNIGHT75,
    },
    error: {
        marginTop: '24px',
        color: COLORS.LT_PEPPER,
        fontSize: '.75rem',
    },
    filePreviews: {
        marginTop: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    newRequestButton: {
        marginTop: '24px',
    },
    noDocumentsText: {
        marginTop: '16px',
        color: COLORS.LT_MIDNIGHT50,
        fontSize: '14px',
    },
    sectionTitle: {
        marginBottom: '16px',
        fontWeight: 600,
        fontSize: '18px',
        color: COLORS.LT_MIDNIGHT,
    },
    asterisk: {
        color: COLORS.LT_PEPPER,
    },
    containerError: {
        borderColor: COLORS.LT_PEPPER,
        borderWidth: '1px',
    },
    textError: {
        color: COLORS.LT_PEPPER,
    },
});

const ExpenseReceiptUpload = (props) => {
    const {
        classes,
        index,
        mode,
        isPosted,
        isPendingSubmission,
        isViewOnly,
        onOCRUpload,
        expense,
        expenseId,
        attachmentType = 'standard',
        isInternal = false,
        showSectionTitle = false,
        sectionTitle = '',
        sectionRequired = false,
        saveAndRegenerateVaultPdf,
        pdfSuccessMessage,
        setPdfSuccessMessage,
    } = props;
    const { register, setValue, errors, getValues, clearErrors, trigger, watch } = useFormContext();

    // Helper to get field paths based on attachment type
    const getFieldPaths = useCallback(() => {
        const baseFieldPath =
            attachmentType === 'standard'
                ? `fileUploads[${index}]`
                : `fileUploads[${index}].${attachmentType}`;

        return {
            baseFieldPath,
            newFilesPath: `${baseFieldPath}.newFiles`,
            existingFilesPath: `${baseFieldPath}.existingFiles`,
        };
    }, [attachmentType, index]);
    const [existingFiles, setExistingFiles] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const [filteredFiles, setFilteredFiles] = useState([]);
    const [error, setError] = useState(null);
    const [fileRejections, setFileRejections] = useState([]);
    const [regeneratingPdf, setRegeneratingPdf] = useState(false);
    const [regenerationMessage, setRegenerationMessage] = useState(null);
    const [showRegenerateButton, setShowRegenerateButton] = useState(
        mode === ExpenseModeEnum.Vault && expense?.billType !== BillTypeEnum.NonBillProducer
    );
    const validationMode = useContext(ValidationModeContext);
    const currentStatus = watch(`expenseEntries[${index}].status`);

    const initialWasApprovedRef = useRef(expense?.status === ExpenseStatusValuesEnum.Approved);

    const bookingBillType = watch(`expenseEntries[${index}].bookingBillType`);
    const billType = watch(`expenseEntries[${index}].billType`);
    const watchedTransactionDate = watch(`expenseEntries[${index}].transactionDate`);
    const watchedMerchantName = watch(`expenseEntries[${index}].merchantName`);
    const watchedAmount = watch(`expenseEntries[${index}].amount`);

    const maxSize = 10 * 1048576;
    const maxFiles = 15;
    const [loadingState, setLoadingState] = useState({});
    const isApproved = currentStatus === ExpenseStatusValuesEnum.Approved;
    const isDisabled =
        JSON.stringify(loadingState) !== '{}' ||
        (isPosted && isViewOnly) ||
        (isApproved && isViewOnly) ||
        (isPendingSubmission && mode !== ExpenseModeEnum.Payment && isViewOnly);
    const bytesToMegaBytes = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

    const requiredUploadMessage = 'Upload is required';

    useEffect(() => {
        // Get field paths based on attachment type
        const { baseFieldPath, newFilesPath, existingFilesPath } = getFieldPaths();

        const fileUploads = getValues(baseFieldPath);
        if (fileUploads) {
            // The resetting of the setValue with  shouldValidate: false and the subsequent trigger
            // was added to get rid of a validation issue that was happening when deleting all images
            // on a middle tab (4) and then deleting a prior tab(3) and having the validation
            // correct on the new tabs post delete.  Might be a better way but this seems to resolve the issue
            setNewFiles(fileUploads.newFiles || []);
            setValue(newFilesPath, fileUploads.newFiles, {
                shouldValidate: false,
            });

            // For categorized uploads (invoice/internal), files are already separated into categories
            // No additional filtering needed since the correct category is accessed via baseFieldPath
            const newExistingFiles = fileUploads.existingFiles || [];
            const filteredExistingFiles = newExistingFiles.filter((file) => !file.isCombinedPdf);
            const combinedPdfFiles = newExistingFiles.filter((file) => file.isCombinedPdf);

            setExistingFiles(filteredExistingFiles);
            setValue(existingFilesPath, fileUploads.existingFiles, {
                shouldValidate: false,
            });
            trigger(newFilesPath);
            trigger(existingFilesPath);
            setFilteredFiles(combinedPdfFiles);
        } else {
            setNewFiles([]);
            setExistingFiles([]);
        }

        // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
        return () => {
            // Cleanup logic here
            if (newFiles && newFiles.length && newFiles.length > 0) {
                newFiles.forEach((file) => URL.revokeObjectURL(file.preview));
            }
        };
    }, [getFieldPaths, attachmentType, isInternal, setValue, trigger, getValues]);

    const convertFile = async (file) => {
        if (file.type === 'image/heic') {
            const fileName = file.name.substring(0, file.name.indexOf('.'));
            const convertedBlob = await heic2any({
                blob: file,
                toType: 'image/jpeg',
                quality: 0.5,
            });
            const newFile = new File([convertedBlob], `${fileName}.jpg`, { type: 'image/jpeg' });
            if (bytesToMegaBytes(newFile.size) > 10) {
                setError('File is too large after HEIC conversion');
                return;
            }
            return newFile;
        }
        return file; // Return non-HEIC files directly
    };

    const onDrop = useCallback(
        async (acceptedFiles) => {
            setError(null);

            // Get field paths based on attachment type
            const { newFilesPath, existingFilesPath } = getFieldPaths();

            clearErrors('fileUploads');

            const checkExistingFiles = getValues(existingFilesPath) || [];
            const filteredExistingFiles = checkExistingFiles.filter((file) => !file.isCombinedPdf);
            const checkNewFiles = getValues(newFilesPath) || [];

            // Get existing file names and sizes for stronger duplicate detection
            const existingFileKeys = new Set([
                ...filteredExistingFiles.map((f) => `${f.originalFilename || f.name}_${f.size}`),
                ...checkNewFiles.map((f) => `${f.name}_${f.size}`),
            ]);

            // Filter out duplicates by name and size
            const uniqueFiles = acceptedFiles.filter(
                (file) => !existingFileKeys.has(`${file.name}_${file.size}`)
            );
            const duplicateFiles = acceptedFiles.filter((file) =>
                existingFileKeys.has(`${file.name}_${file.size}`)
            );

            if (duplicateFiles.length > 0) {
                setError('Duplicate files are not allowed (same name and size)');
                return;
            }

            // Clear previous state if starting a new upload session
            if (checkNewFiles.length === 0 && filteredExistingFiles.length === 0) {
                setNewFiles([]);
                setExistingFiles([]);
            }

            setFileRejections([]);
            if (
                uniqueFiles.length + checkNewFiles.length + filteredExistingFiles.length >
                    maxFiles ||
                checkNewFiles.length + filteredExistingFiles.length === maxFiles
            ) {
                setError('Max file limit reached');
                return;
            }

            let newIsLoading = { ...loadingState };

            // Add isInternal flag to new files for categorized uploads while preserving all properties
            const filesWithMetadata = uniqueFiles.map((file) => {
                const newFile = file;
                newFile.isInternal = isInternal;

                return newFile;
            });

            setValue(newFilesPath, [...newFiles, ...filesWithMetadata]);
            setNewFiles([...newFiles, ...filesWithMetadata]);

            const conversionPromises = uniqueFiles.map(async (file) => {
                const sizeInMB = bytesToMegaBytes(file.size);
                let duration;
                if (sizeInMB >= 4.3) {
                    duration = 19;
                } else if (sizeInMB >= 3.5) {
                    duration = 15;
                } else {
                    duration = 12.5;
                }
                newIsLoading = { ...newIsLoading, [file.name]: { isLoading: true, duration } };
                setLoadingState(newIsLoading); // Set loading for this file

                try {
                    const convertedFile = await convertFile(file);
                    Object.assign(convertedFile, {
                        preview: URL.createObjectURL(convertedFile),
                    });
                    return convertedFile;
                } catch (err) {
                    console.error('Error converting HEIC image:', err);
                } finally {
                    const allOcrFieldsPopulated =
                        !!watchedTransactionDate && !!watchedMerchantName && !!watchedAmount;
                    if (!allOcrFieldsPopulated) {
                        try {
                            const ocrResponse = await postImageForOCR(file, 'receipt');
                            if (ocrResponse) {
                                onOCRUpload(ocrResponse);
                            }
                        } catch (err) {
                            console.error('Error posting image for OCR:', err);
                        }
                    }

                    newIsLoading = {
                        ...newIsLoading,
                        [file.name]: { ...newIsLoading[file.name], isLoading: false },
                    };
                    setLoadingState(newIsLoading); // Clear loading for this file
                }
            });

            // Wait for all conversions to finish using await
            const convertedFiles = await Promise.all(conversionPromises);

            // Add isInternal flag to converted files while preserving all properties
            const convertedFilesWithMetadata = convertedFiles.map((file) => {
                if (!file) return file;
                // Simply add the isInternal property to the existing file object
                const newFile = file;
                newFile.isInternal = isInternal;
                return newFile;
            });

            const allFiles = [...newFiles, ...convertedFilesWithMetadata];
            setLoadingState({});

            // Use field paths for setValue
            setValue(getFieldPaths().newFilesPath, allFiles, {
                shouldValidate: true,
                shouldDirty: true,
            });
            setNewFiles(allFiles);
            trigger(`fileUploads[${index}]`);
        },
        [
            setValue,
            newFiles,
            attachmentType,
            isInternal,
            getFieldPaths,
            watchedTransactionDate,
            watchedMerchantName,
            watchedAmount,
        ]
    );

    const onError = useCallback(
        (err) => {
            setError(err);
        },
        [setValue, newFiles]
    );

    const paymentRequestValidationRequired = () => sectionRequired;

    const hasFilesUploaded = (value, files) => value && value.length + (files?.length || 0) > 0;

    useEffect(() => {
        const { newFilesPath, existingFilesPath } = getFieldPaths();

        register(newFilesPath, {
            validate: {
                required: (value) => {
                    if (isInternal) return true;
                    if (mode === ExpenseModeEnum.Payment && !paymentRequestValidationRequired())
                        return true;

                    if (
                        mode === ExpenseModeEnum.Vault &&
                        !(
                            billType === BillTypeEnum.Billable &&
                            currentStatus === ExpenseStatusValuesEnum.Approved
                        )
                    )
                        return true;

                    const checkExistingFiles = getValues(existingFilesPath);
                    const filteredExistingFiles =
                        checkExistingFiles?.filter((file) => !file.isCombinedPdf) || [];
                    const total = (value?.length ?? 0) + filteredExistingFiles.length;

                    if (sectionRequired && mode === ExpenseModeEnum.Vault && total === 0) {
                        return requiredUploadMessage;
                    }
                    if (!hasFilesUploaded(value, filteredExistingFiles)) {
                        if (
                            mode === ExpenseModeEnum.CreditCard &&
                            (billType === BillTypeEnum.Billable ||
                                billType === BillTypeEnum.ClinicianDeduction)
                        )
                            return currentStatus !== ExpenseStatusValuesEnum.Approved &&
                                currentStatus !== ExpenseStatusValuesEnum.InvoiceReview
                                ? true
                                : requiredUploadMessage;

                        if (sectionRequired) return requiredUploadMessage;
                    }
                },
            },
        });
        register(existingFilesPath, {
            validate: {
                required: (value) => {
                    if (isInternal) return true;
                    if (mode === ExpenseModeEnum.Payment && !paymentRequestValidationRequired())
                        return true;

                    if (
                        mode === ExpenseModeEnum.Vault &&
                        !(
                            billType === BillTypeEnum.Billable &&
                            currentStatus === ExpenseStatusValuesEnum.Approved
                        )
                    )
                        return true;

                    const checkNewFiles = getValues(newFilesPath);

                    if (!hasFilesUploaded(value, checkNewFiles)) {
                        if (
                            mode === ExpenseModeEnum.CreditCard &&
                            (billType === BillTypeEnum.Billable ||
                                billType === BillTypeEnum.ClinicianDeduction)
                        )
                            return currentStatus !== ExpenseStatusValuesEnum.Approved &&
                                currentStatus !== ExpenseStatusValuesEnum.InvoiceReview
                                ? true
                                : requiredUploadMessage;

                        if (sectionRequired) return requiredUploadMessage;
                    }
                },
            },
        });
        return () => {
            setError(null);
            setFileRejections([]);
        };
    }, [
        register,
        sectionRequired,
        validationMode,
        getFieldPaths,
        getValues,
        mode,
        billType,
        currentStatus,
    ]);

    const customValidation = (file) => {
        const isValidSize = file.size <= maxSize;
        if (!isValidSize)
            return { code: 'invalid-file-size', message: 'File is larger than 10 MB' };
        return null;
    };

    const onDropRejected = useCallback((rejections) => {
        setFileRejections(rejections);
    });

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        onError,
        onDropRejected,
        accept: {
            'image/jpeg': [],
            'image/png': [],
            'image/heic': [],
            'application/pdf': [],
        },
        disabled: isViewOnly || isDisabled,
        validator: customValidation,
    });

    const removeFile = async (fileIndex, isExisting) => {
        setError(null);
        const { newFilesPath, existingFilesPath } = getFieldPaths();

        clearErrors(existingFilesPath);
        clearErrors(newFilesPath);

        if (mode === ExpenseModeEnum.Vault) {
            setExistingFiles([]);
            setNewFiles([]);
            setValue(existingFilesPath, [], {
                shouldValidate: true,
                shouldDirty: true,
            });
            setValue(newFilesPath, [], {
                shouldValidate: true,
                shouldDirty: true,
            });
            setLoadingState({});
            await deleteExpenseImageById(
                existingFiles[fileIndex].expenseId,
                existingFiles[fileIndex].id,
                false // precoded
            );
            return; // Exit early
        }
        if (isExisting) {
            if (existingFiles && existingFiles.length && existingFiles.length > 0) {
                const existingFile = existingFiles[fileIndex];
                setLoadingState((prevState) => ({
                    ...prevState,
                    [existingFile.name]: { isLoading: true },
                }));
                const aExistingFiles = existingFiles.filter((_existingFile, i) => i !== fileIndex);
                setExistingFiles(aExistingFiles);
                setValue(existingFilesPath, aExistingFiles, {
                    shouldValidate: true,
                    shouldDirty: true,
                });
            }
        } else if (newFiles && newFiles.length && newFiles.length > 0) {
            const newFile = newFiles[fileIndex];
            setLoadingState((prevState) => ({ ...prevState, [newFile.name]: { isLoading: true } }));
            const aNewFiles = newFiles.filter((_file, i) => i !== fileIndex);
            setNewFiles(aNewFiles);
            setValue(newFilesPath, aNewFiles, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }
        setLoadingState({});
    };

    const handleRegeneratePdf = async () => {
        if (!saveAndRegenerateVaultPdf) {
            setPdfSuccessMessage('Error: Save and regenerate function not available');
            return;
        }

        setRegeneratingPdf(true);
        setRegenerationMessage(null);

        try {
            await saveAndRegenerateVaultPdf();
            setPdfSuccessMessage('PDF generated successfully! Showing latest attachments...');
            // Fetch latest attachments and update state
            if (expenseId) {
                const latestExpense = await import('../../Services/ExpenseService').then((m) =>
                    m.getVaultExpensesById(expenseId)
                );
                if (latestExpense && latestExpense.images) {
                    const filteredExistingFiles = latestExpense.images.filter(
                        (file) => !file.isCombinedPdf
                    );
                    const combinedPdfFiles = latestExpense.images.filter(
                        (file) => file.isCombinedPdf
                    );
                    setExistingFiles(filteredExistingFiles);
                    setFilteredFiles(combinedPdfFiles);
                }
            }

            // Optionally, refresh the page after a delay
            setTimeout(() => {
                setPdfSuccessMessage(null);
            }, 3000);
        } catch (e) {
            const errorMessage = formatErrorMessage(e);
            setPdfSuccessMessage(`Error generating PDF: ${errorMessage}`);
        } finally {
            setRegeneratingPdf(false);
        }
    };

    const hasUploadErrors =
        !isInternal &&
        !!(
            errors?.fileUploads?.[index]?.newFiles ||
            errors?.fileUploads?.[index]?.existingFiles ||
            errors?.fileUploads?.[index]?.invoice?.newFiles ||
            errors?.fileUploads?.[index]?.invoice?.existingFiles
        );

    useEffect(() => {
        if (mode !== ExpenseModeEnum.Vault) {
            setShowRegenerateButton(false);
            return;
        }

        if (bookingBillType === BillTypeEnum.NonBillProducer) {
            setShowRegenerateButton(false);
            return;
        }

        if (expense?.billType === BillTypeEnum.NonBillProducer) {
            setShowRegenerateButton(false);
            return;
        }

        if (isPosted) {
            setShowRegenerateButton(false);
            return;
        }

        if (initialWasApprovedRef.current) {
            setShowRegenerateButton(false);
            return;
        }

        setShowRegenerateButton(true);
    }, [mode, bookingBillType, expense?.billType, isPosted]);

    return (
        <Paper
            classes={{
                root: [classes.PaperRoot, hasUploadErrors ? classes.containerError : ''].join(' '),
            }}
            variant={hasUploadErrors ? 'outlined' : 'elevation'}
        >
            {/* Section Title */}
            {showSectionTitle && sectionTitle && (
                <Typography
                    variant="h5"
                    component="h5"
                    className={[
                        classes.sectionTitle,
                        hasUploadErrors ? classes.textError : '',
                    ].join(' ')}
                >
                    {sectionTitle}
                    {sectionRequired && <span className={classes.asterisk}> *</span>}
                </Typography>
            )}

            <Grid2
                {...getRootProps({
                    className: `${classes.outside} ${(isViewOnly || isDisabled) && 'disabled'}`,
                })}
            >
                <Grid2 className={classes.centerDrop}>
                    <Grid2 className={classes.dot}>
                        <PublishIcon className={classes.icon} />
                    </Grid2>
                    <Typography variant="body2" className={classes.uploadTitle}>
                        <u>Click to Upload</u> or Drag and Drop
                    </Typography>
                    <Typography variant="body2" className={classes.uploadDescription}>
                        Max file size: 10MB • .pdf, .heic, .jpeg, .png
                    </Typography>
                    <input
                        {...getInputProps()}
                        name={getFieldPaths().newFilesPath}
                        type="file"
                        disabled={isViewOnly || isDisabled}
                    />
                </Grid2>
            </Grid2>
            {error && (
                <Typography variant="body2" className={classes.error}>
                    <b>{error?.message ? error.message : error}</b>
                </Typography>
            )}

            {/* Show "No documents attached" when there are no files */}
            {!newFiles?.length && !existingFiles?.length && (
                <Typography variant="body2" className={classes.noDocumentsText}>
                    No documents attached
                </Typography>
            )}

            {(!!newFiles?.length > 0 || !!existingFiles?.length > 0) && (
                <div className={classes.filePreviews}>
                    {filteredFiles.length > 0 && (
                        <div>
                            <Typography variant="body2" display="flex" alignItems="center">
                                Combined PDF
                                <Tooltip
                                    title="The combined PDF, which includes all uploaded expense files, regenerates when you save after adding or removing files"
                                    arrow
                                >
                                    <InfoOutlinedIcon
                                        style={{
                                            marginLeft: 8,
                                            cursor: 'pointer',
                                            fontSize: 'small',
                                        }}
                                    />
                                </Tooltip>
                            </Typography>
                            {filteredFiles.map((file, i) => (
                                <FilePreview
                                    key={file.filename}
                                    fileIndex={i}
                                    file={file}
                                    isExisting
                                    loadingState={loadingState}
                                    isDisabled
                                    removeFile={removeFile}
                                    mode={mode}
                                    isCombinedPdf
                                />
                            ))}
                        </div>
                    )}
                    {mode === ExpenseModeEnum.Vault ? (
                        <Typography variant="body2">Uploaded Files</Typography>
                    ) : (
                        <Typography variant="body2">
                            {(newFiles && newFiles.length ? newFiles.length : 0) +
                                (existingFiles && existingFiles.length
                                    ? existingFiles.length
                                    : 0)}{' '}
                            of {maxFiles} files
                        </Typography>
                    )}
                    {existingFiles.map((file, i) => (
                        <FilePreview
                            key={file.filename + file.id}
                            fileIndex={i}
                            file={file}
                            isExisting
                            loadingState={loadingState}
                            isDisabled={isViewOnly || isDisabled}
                            removeFile={removeFile}
                            mode={mode}
                            isCombinedPdf={false}
                        />
                    ))}
                    {newFiles.map((file, i) => (
                        <FilePreview
                            key={file.name}
                            fileIndex={i}
                            file={file}
                            isExisting={false}
                            loadingState={loadingState}
                            isDisabled={isViewOnly || isDisabled}
                            removeFile={removeFile}
                            mode={mode}
                            isCombinedPdf={false}
                        />
                    ))}
                </div>
            )}

            {fileRejections?.length > 0 && <FileRejections fileRejections={fileRejections} />}

            {hasUploadErrors &&
                (() => {
                    const { newFilesPath, existingFilesPath } = getFieldPaths();
                    const newFilesPathArray = newFilesPath.split('.');
                    const existingFilesPathArray = existingFilesPath.split('.');

                    // Navigate through nested error object
                    let newFilesError = errors;
                    let existingFilesError = errors;

                    Object.keys(newFilesPathArray).forEach((key) => {
                        newFilesError = newFilesError?.[key];
                    });

                    Object.keys(existingFilesPathArray).forEach((key) => {
                        existingFilesError = existingFilesError?.[key];
                    });

                    const primaryMsg = newFilesError?.message || existingFilesError?.message || '';

                    if (primaryMsg) {
                        return (
                            <Typography variant="body2" className={classes.error}>
                                {primaryMsg}
                            </Typography>
                        );
                    }

                    const fallbackMsg =
                        errors?.fileUploads?.[index]?.newFiles?.message ??
                        errors?.fileUploads?.[index]?.existingFiles?.message ??
                        errors?.fileUploads?.[index]?.invoice.newFiles?.message ??
                        errors?.fileUploads?.[index]?.invoice.existingFiles?.message ??
                        errors?.fileUploads?.[index]?.newFiles?.types?.required ??
                        errors?.fileUploads?.[index]?.existingFiles?.types?.required ??
                        errors?.fileUploads?.[index]?.newFiles?.types?.requiredVault ??
                        errors?.fileUploads?.[index]?.existingFiles?.types?.requiredVault ??
                        '';

                    return fallbackMsg ? (
                        <Typography variant="body2" className={classes.error}>
                            {fallbackMsg}
                        </Typography>
                    ) : null;
                })()}

            {showRegenerateButton && !isViewOnly && (
                <>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.newRequestButton}
                        onClick={handleRegeneratePdf}
                        disabled={regeneratingPdf}
                    >
                        {regeneratingPdf ? 'Regenerating...' : 'Request New Receipt'}
                    </Button>
                    {pdfSuccessMessage && (
                        <Typography
                            variant="body2"
                            style={{
                                marginTop: '8px',
                                color: pdfSuccessMessage.includes('Error') ? 'red' : 'green',
                            }}
                        >
                            {pdfSuccessMessage}
                        </Typography>
                    )}
                </>
            )}
        </Paper>
    );
};

export default withStyles(styles)(ExpenseReceiptUpload);
